// VER-218: Console session state (access token in memory + user), exposed via
// React context. Bootstraps from the httpOnly refresh cookie on load.
import { useEffect, useState, useCallback } from "react";
import { authApi, inviteApi, setOnSessionExpired } from "./consoleApi";
import { setAccessToken, clearAccessToken } from "./tokenStore";
import { clearStepUpToken } from "./stepUpStore";
import { SessionContext } from "./useSession";
import { brandMismatch, BrandMismatchError } from "../brand/brandConfig";

// 3a bridge: mirror the session user's api-key into the localStorage keys the
// LEGACY dashboard (and PrivateRoute) still read, so existing x-api-key screens
// keep working after a single Console login. Cleared on logout; retired at the
// Step-12 cutover once every screen is session-native.
// Map a Console role to the value the LEGACY dashboard's guards expect:
// RoleBasedRoute checks localStorage.user_role against ["admin"] (default "client").
function toLegacyRole(role) {
  return role === "account_admin" || role === "internal_admin" ? "admin" : "client";
}

function writeLegacyBridge(user) {
  if (user?.apiKey) localStorage.setItem("apiKey", user.apiKey);
  localStorage.setItem("isAuthenticated", "true");
  localStorage.setItem("user_role", toLegacyRole(user?.role));
  // Marker for the legacy axios interceptor: while a Console session is active, a
  // legacy /api 401 must NOT wipe storage + bounce to /login (that would kill the
  // real, in-memory Console session). Retired at the Step-12 cutover.
  localStorage.setItem("vu_console_active", "1");
}
function clearLegacyBridge() {
  localStorage.removeItem("apiKey");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("user_role");
  localStorage.removeItem("vu_console_active");
}

export function SessionProvider({ children }) {
  const [user, setUser] = useState(null);
  // loading -> we don't yet know if there's a session; authenticated | unauthenticated
  const [status, setStatus] = useState("loading");

  const applySession = useCallback(async (access, userObj) => {
    // Cross-brand guard: this endpoint is brand-scoped (e.g. console.vaultro.ai),
    // so an account belonging to a different brand must NOT be signed in here. The
    // OTP verify / invite accept / refresh has already minted a session, so tear it
    // down server-side + locally, then reject so the caller surfaces the error.
    const mismatch = brandMismatch(userObj);
    if (mismatch) {
      if (access) setAccessToken(access); // ensure the logout call is authenticated
      try {
        await authApi.logout();
      } catch {
        /* best-effort: still clear locally below */
      }
      clearAccessToken();
      clearStepUpToken();
      clearLegacyBridge();
      setUser(null);
      setStatus("unauthenticated");
      throw new BrandMismatchError(mismatch.host);
    }

    if (access) setAccessToken(access);
    setUser(userObj);
    writeLegacyBridge(userObj);
    setStatus("authenticated");
  }, []);

  // Bootstrap: re-mint an access token from the refresh cookie, then load /me.
  // No valid session (or never logged into the Console) -> unauthenticated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.refresh();
        if (cancelled) return;
        setAccessToken(r.data.data.access);
        const meRes = await authApi.me();
        if (cancelled) return;
        await applySession(null, meRes.data.data.user);
      } catch {
        if (!cancelled) {
          clearAccessToken();
          setStatus("unauthenticated");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  // Hard logout when the client can't refresh (idle/absolute expiry or revoked).
  useEffect(() => {
    setOnSessionExpired(() => {
      clearStepUpToken();
      clearLegacyBridge();
      setUser(null);
      setStatus("unauthenticated");
    });
  }, []);

  // Step 1 of login: password -> triggers an OTP. Returns { otp_required, otp_token }.
  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    return res.data.data;
  }, []);

  // Step 2 of login: OTP -> establishes the session.
  const verifyOtp = useCallback(
    async (otpToken, code) => {
      const res = await authApi.verifyOtp(otpToken, code);
      const { access, user: u } = res.data.data;
      await applySession(access, u);
      return u;
    },
    [applySession]
  );

  const resendOtp = useCallback((otpToken) => authApi.resendOtp(otpToken), []);

  // Invite acceptance: validate the token (for the set-password page), then accept
  // with a password — which establishes the session, same as an OTP login.
  const validateInvite = useCallback((token) => inviteApi.validate(token), []);

  const acceptInvite = useCallback(
    async (token, password) => {
      const res = await inviteApi.accept(token, password);
      const { access, user: u } = res.data.data;
      await applySession(access, u);
      return u;
    },
    [applySession]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore network errors on logout */
    }
    clearAccessToken();
    clearStepUpToken();
    clearLegacyBridge();
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const value = {
    status,
    user,
    login,
    verifyOtp,
    resendOtp,
    validateInvite,
    acceptInvite,
    logout,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
